/**
 * conso-search-tracking.js
 * Rastreia a busca (dropdown preditivo + submit) para o GA4 via GTM.
 *
 * Por que existe: o dropdown do Horizon busca via fetch em /search/suggest e
 * injeta o HTML pelo section renderer. Não é navegação, então nem o analytics
 * nativo do Shopify nem o page_view do GA4 enxergam. Quem acha o produto no
 * dropdown e clica nunca carrega /search — o termo digitado se perde.
 *
 * Aditivo: não altera markup nem comportamento da busca. Observa o container
 * de resultados e delega cliques no document (fase de captura), porque o
 * dropdown é remontado por morph a cada tecla e listeners ligados direto nos
 * elementos seriam perdidos.
 *
 * Eventos empurrados:
 *   search                 search_term, results_count, products_count, has_results
 *   search_no_results      search_term
 *   select_search_result   search_term, position, item_name, item_url, item_type
 *   search_submit          search_term  (Enter ou "Ver tudo" → /search)
 *
 * `search_term` é o nome de parâmetro que o GA4 usa no relatório nativo de
 * busca — por isso não abreviei para `term`.
 */
(function () {
  'use strict';

  var MAX_LEN = 100; // limite de tamanho de valor de parâmetro do GA4
  var DEBOUNCE_MS = 600; // espera o usuário parar de digitar antes de contar

  /** Container dos resultados reais. O estado inicial (vitrine/vistos
   *  recentemente) usa o mesmo id mas NÃO tem esse atributo. */
  var RESULTS = '[data-search-results]';
  var PRODUCT_CARD = '.predictive-search-results__card--product';
  var ANY_CARD = '.predictive-search-results__card';
  var NO_RESULTS = '.predictive-search-results__no-results';

  var debounceTimer = null;
  var lastKey = ''; // dedupe: o morph redispara mutação sem o termo ter mudado

  function clean(value) {
    if (!value) return '';
    return String(value).replace(/\s+/g, ' ').trim().slice(0, MAX_LEN);
  }

  function push(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function pathOf(href) {
    if (!href) return '';
    try {
      return clean(new URL(href, window.location.origin).pathname);
    } catch (e) {
      return clean(href);
    }
  }

  /** Tipo do recurso a partir da URL — mais confiável que data-attribute aqui,
   *  porque data-product-id só existe com transition_to_main_product ligado
   *  (está desligado na loja por conta do app Varia). */
  function typeFromPath(path) {
    if (path.indexOf('/products/') === 0) return 'product';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path.indexOf('/blogs/') === 0) return 'article';
    if (path.indexOf('/pages/') === 0) return 'page';
    return 'other';
  }

  /** Termo digitado, lido do input do modal (não do input da página /search). */
  function termOf(scope) {
    var root = scope && scope.closest ? scope.closest('predictive-search-component') : null;
    var input = (root || document).querySelector('.predictive-search-form .search-input');
    return input ? clean(input.value) : '';
  }

  /**
   * O container de resultados também é usado para "vistos recentemente" (quando
   * o campo está vazio o tema busca produtos por id). Isso não é uma busca do
   * usuário e não deve virar evento.
   */
  function isRecentlyViewed(container) {
    return !!container.querySelector('.recently-viewed-wrapper');
  }

  function report(container) {
    if (isRecentlyViewed(container)) return;

    var term = termOf(container);
    if (!term) return;

    var products = container.querySelectorAll(PRODUCT_CARD).length;
    var total = container.querySelectorAll(ANY_CARD).length;
    var empty = !!container.querySelector(NO_RESULTS);

    var key = term + '|' + total + '|' + products;
    if (key === lastKey) return;
    lastKey = key;

    push({
      event: 'search',
      search_term: term,
      results_count: total,
      products_count: products,
      has_results: !empty && total > 0
    });

    // Evento separado para busca sem retorno: é o sinal mais acionável de CRO
    // (demanda sem cobertura, nome errado de categoria, sinônimo faltando) e
    // dá para alertar em cima dele.
    if (empty || total === 0) {
      push({ event: 'search_no_results', search_term: term });
    }
  }

  function scheduleReport(container) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      report(container);
    }, DEBOUNCE_MS);
  }

  /** O container é substituído por morph a cada digitação, então observamos o
   *  componente inteiro em vez de um nó que deixaria de existir. */
  function watch(component) {
    if (component.dataset.consoSearchWatched) return;
    component.dataset.consoSearchWatched = '1';

    new MutationObserver(function () {
      var container = component.querySelector(RESULTS);
      if (container) scheduleReport(container);
    }).observe(component, { childList: true, subtree: true });
  }

  function watchAll() {
    var list = document.querySelectorAll('predictive-search-component');
    for (var i = 0; i < list.length; i++) watch(list[i]);
  }

  function onClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var card = target.closest(ANY_CARD);
    if (!card) return;

    var container = card.closest(RESULTS);
    if (!container || isRecentlyViewed(container)) return;

    var anchor = card.querySelector('a[href]') || target.closest('a[href]');
    if (!anchor) return;

    var siblings = container.querySelectorAll(ANY_CARD);
    var position = 0;
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i] === card) {
        position = i + 1; // 1-based, como o GA4 espera em index de lista
        break;
      }
    }

    var path = pathOf(anchor.getAttribute('href'));

    push({
      event: 'select_search_result',
      search_term: termOf(card),
      position: position,
      item_name: clean(card.textContent),
      item_url: path,
      item_type: typeFromPath(path)
    });
  }

  var lastSubmit = 0;

  function reportSubmit(term) {
    if (!term) return;
    var now = Date.now();
    if (now - lastSubmit < 1000) return; // Enter pode disparar keydown + submit
    lastSubmit = now;
    push({ event: 'search_submit', search_term: term });
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.classList || !form.classList.contains('predictive-search-form')) return;
    reportSubmit(termOf(form));
  }

  /**
   * O botão "Ver tudo" submete o form nativamente, mas o Enter não: o
   * predictive-search.js intercepta e navega com window.location. Sem esse
   * listener metade dos submits ficaria de fora.
   */
  function onKeydown(event) {
    if (event.key !== 'Enter') return;

    var input = event.target;
    if (!input || !input.classList || !input.classList.contains('search-input')) return;

    var root = input.closest('predictive-search-component');
    if (!root) return;

    // Enter com item destacado navega para o item — isso já é select_search_result.
    if (root.querySelector('[aria-selected="true"]')) return;

    reportSubmit(clean(input.value));
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('keydown', onKeydown, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAll);
  } else {
    watchAll();
  }
})();
